import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import {
  renderBarcodeSvg,
  LABEL_BARCODE_SVG_MAX_HEIGHT_MM,
  LABEL_BARCODE_PREVIEW_JS_OPTIONS,
} from '../../utils/inventoryLabelBarcode';
import {
  LABEL_PRINT_WIDTH_MM,
  LABEL_PRINT_HEIGHT_MM,
  LABEL_SEG_ADHESIVE_MM,
  LABEL_SEG_INFO_MM,
  LABEL_SEG_BARCODE_MM,
} from '../../utils/printInventoryLabels';

/** Campos mínimos para renderizar la etiqueta 94×11 mm (misma geometría que impresión). */
export interface InventoryLabelPreviewRow {
  productCodeUnique: string;
  materialLabel: string;
  salePrice: number;
  weightGrams: number | null;
}

export function InventoryLabelPreviewMini({ row }: { row: InventoryLabelPreviewRow }) {
  const svgRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    renderBarcodeSvg(row.productCodeUnique ? svgRef.current : null, row.productCodeUnique ?? '', {
      ...LABEL_BARCODE_PREVIEW_JS_OPTIONS,
    });
  }, [row.productCodeUnique]);

  const priceStr = `Bs. ${Number(row.salePrice).toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
  const w =
    row.weightGrams != null && !Number.isNaN(Number(row.weightGrams))
      ? Number(row.weightGrams).toLocaleString('es-BO', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      : '—';

  return (
    <Box
      sx={{
        width: `${LABEL_PRINT_WIDTH_MM}mm`,
        height: `${LABEL_PRINT_HEIGHT_MM}mm`,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        p: '0.5mm 0.6mm',
        bgcolor: '#fff',
        color: '#111827',
        fontFamily: '"Times New Roman", Times, serif',
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box
        aria-hidden
        sx={{
          flex: `${LABEL_SEG_ADHESIVE_MM} 1 0%`,
          minWidth: 0,
        }}
      />
      <Box
        sx={{
          flex: `${LABEL_SEG_INFO_MM} 1 0%`,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '0.15mm',
          pr: '0.8mm',
          overflow: 'hidden',
        }}
      >
        <Typography sx={{ fontStyle: 'italic', fontSize: '7pt', lineHeight: 1.1 }} noWrap>
          {row.materialLabel}
        </Typography>
        <Typography sx={{ fontSize: '7pt', lineHeight: 1.1 }} noWrap>
          {priceStr}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: `${LABEL_SEG_BARCODE_MM} 1 0%`,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
          px: '0.8mm',
        }}
      >
        <Box
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            maxWidth: '100%',
          }}
        >
          <svg
            ref={svgRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              maxHeight: `${LABEL_BARCODE_SVG_MAX_HEIGHT_MM}mm`,
              display: 'block',
            }}
          />
        </Box>
        <Box
          sx={{
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            fontSize: '5.5pt',
          }}
        >
          <span>{w}</span>
          <span style={{ textDecoration: 'underline' }}>{row.productCodeUnique}</span>
        </Box>
      </Box>
    </Box>
  );
}
